import React, { useState } from 'react';
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { getFirestore, getDoc, doc, setDoc } from 'firebase/firestore';
import logo from '../assets/wycoughlogo.png';
import { useNavigate } from 'react-router-dom';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // List of email addresses that should have admin access
  // Add your email address to this list
  const adminEmails = [
    'bramesar5@gmail.com', 'cloudyvisioninc@gmail.com' // Replace with your actual email
    // Add more admin emails as needed
  ];

  const handleForgotPassword = () => {
    const email = prompt("Please enter your email to reset password:");
    if (email) {
      sendPasswordResetEmail(auth, email)
        .then(() => alert("Password reset email sent!"))
        .catch((error) => alert("Error: " + error.message));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Sign in with Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user's email is in our admin list
      const isAdminByEmail = adminEmails.includes(user.email);
      
      if (isAdminByEmail) {
        // If email is in admin list, create/update user document with admin role
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          role: 'admin',
          updatedAt: new Date()
        }, { merge: true });
        
        console.log("Admin access granted to:", user.email);
        navigate('/admin/dashboard');
        return;
      }
      
      // If not admin by email, check Firestore and JWT claims
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create user document if it doesn't exist yet
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          role: 'user',
          createdAt: new Date()
        });
        
        // Not an admin user
        await auth.signOut();
        setError('Access denied. You do not have admin privileges.');
        return;
      }
      
      const userData = userDoc.data();
      const idTokenResult = await user.getIdTokenResult();
      
      if (userData.role === 'admin' || idTokenResult.claims.admin) {
        // User is an admin, redirect to dashboard
        navigate('/admin/dashboard');
      } else {
        // User is not an admin, show error
        await auth.signOut();
        setError('Access denied. You do not have admin privileges.');
      }
    } catch (error) {
      console.error("Login error:", error);
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account exists with this email. Please check your email.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address. Please check your email.');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        default:
          setError(error.message || 'Failed to login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Debug button to help troubleshoot admin access
  const handleDebug = async () => {
    try {
      alert('Please log in first, then click "Debug Admin" again');
      console.log('Current user:', auth.currentUser?.email);
      
      if (auth.currentUser) {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        console.log('User document exists:', userDoc.exists());
        console.log('User data:', userDoc.data());
        
        const idTokenResult = await auth.currentUser.getIdTokenResult();
        console.log('Admin claim:', idTokenResult.claims.admin);
      }
    } catch (error) {
      console.error('Debug error:', error);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-2">
      <div className="fixed -z-10 top-0 -left-4 w-72 h-72 bg-gradient-to-r from-blue-500 to-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
      <div className="fixed -z-10 -bottom-8 right-4 w-72 h-72 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>

      <div className="relative z-10 bg-white rounded-lg p-4 sm:p-8 shadow-lg backdrop-blur-sm bg-white/90 border border-gray-100">
      <div className="text-center mb-8">
  <div className="logo flex items-center justify-center h-24 sm:h-32 w-32 sm:w-40 mx-auto">
    <img 
      src={logo} 
      alt="Logo" 
      className="h-auto w-auto max-h-full max-w-full object-contain" 
      style={{ transform: 'scale(2.5)' }} // Makes the image 30% larger
    />
  </div>
  <h2 className="text-2xl font-bold mt-4 text-gray-800">Admin Login</h2>
  <p className="text-gray-600">Please sign in with your admin credentials</p>
</div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border-l-4 border-red-500 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative flex items-center">
              <div className="absolute left-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                style={{ paddingLeft: '2.5rem' }}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.135.198-2.222.563-3.222M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              <div className="absolute left-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-4 pl-16 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                style={{ paddingLeft: '2.5rem' }}
                required
              />
            </div>
          </div>

          <button 
            type="submit" 
            className={`w-full p-4 rounded-lg bg-gradient-to-r from-blue-500 to-green-500 text-white font-medium
              transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Logging in...
              </div>
            ) : 'Login as Admin'}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="flex-1 text-sm text-blue-600 hover:underline"
          >
            Forgot Password?
          </button>
          
          {/* Debug button for troubleshooting */}
          <button
            type="button"
            onClick={handleDebug}
            className="flex-1 text-sm text-purple-600 hover:underline"
          >
            Debug Admin
          </button>
        </div>

        <div className="mt-6 text-center">
          <a 
            href="/"
            className="font-medium text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-green-500 hover:opacity-80 transition-opacity"
          >
            Return to Main Website
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;